import axios from 'axios';
import Moment from 'moment';
import api from '../api';
import parser from '../services/parser';
import decompress from '../services/socket/decompress';
import Translations from '../services/translations';
import Socket from '../services/socket';
import Gateway from '../services/gravityGateway';
import helpers from '../services/helpers';
import router from '../router';

export default {
  fetchMeta: ({ commit, getters }, payload) => {
    const { locale, uuid } = getters.getCompany;
    api.loadMeta(payload, { uuid, locale }).then((response) => {
      const parsedData = parser.formatMeta(response);
      commit('INIT_META', parsedData);
    });
  },
  fetchMatch: ({ commit }, payload) => {
    api.loadMatch(payload).then((response) => {
      const parsedData = parser.formatMatch(response);
      commit('INIT_MATCH', parsedData);
    });
  },
  fetchOffer: ({ commit, getters }, payload) => {
    commit('META_LOADED', false);
    commit('TOGGLE_OFFER_LOAD', false);
    const metaParams = _.merge({}, payload);
    const { locale, uuid } = getters.getCompany;

    api.loadMeta(metaParams, { uuid, locale }).then((metaResponse) => {
      const meta = parser.formatMeta(metaResponse);
      commit('INIT_META', meta);
    });

    api.loadOffer(payload).then((response) => {
      const offer = parser.formatOffer(response, false, getters);
      commit('INIT_OFFER', offer);
      commit('TOGGLE_OFFER_LOAD', true);
    });
  },
  fetchTopOffer: ({ commit, getters }, payload) => {
    commit('TOGGLE_OFFER_LOAD', false);
    const { locale, uuid } = getters.getCompany;

    api.loadTopOffer(payload, { uuid, locale }).then((response) => {
      const parsedData = parser.formatTopOffer(response);
      commit('INIT_TOP_OFFER', parsedData);
      commit('TOGGLE_OFFER_LOAD', true);
      commit('META_LOADED', true);
    });
  },
  fetchOutrightsMeta: ({ commit }, payload) => {
    api.loadOutrightsMeta(payload).then((response) => {
      const parsedData = parser.formatMeta(response);
      commit('INIT_OUTRIGHTS_META', parsedData);
    });
  },
  fetchOutrights: ({ commit }, payload) => {
    api.loadOutrights(payload).then((response) => {
      const parsedData = parser.formatOutrights(response);
      commit('INIT_OUTRIGHTS', parsedData);
    });
  },
  fetchConfiguration: ({ commit, getters }, payload) => {
    api.loadConfiguration(payload).then((configuration) => {
      commit('INIT_CONFIG', configuration);

      const cpvUuid = getters.getCpvUuid;
      const company = getters.getCompany;

      axios.get(configuration.config.bonusConfigUrl, {
        params: {
          cpvUuid,
          playerUuid: company.uuid,
        },
      }).then((bonusData) => {
        commit('INIT_BONUS_CONFIG', bonusData);
      });
    });
  },
  loadTranslations: ({ commit }, payload) => {
    commit('LOAD_TRANSLATIONS', payload.data);
  },
  clearBetslip: ({ commit, getters }) => {
    commit('CLEAR_BETSLIP');

    const { getProductConfig, getCompanyConfig } = getters;
    if (getProductConfig) {
      const defaultTicketType = parseInt(getCompanyConfig.ticket.activeType, 10);
      commit('CHANGE_TICKET_TYPE', defaultTicketType);
    }
  },
  ticketRebet: ({ commit }, ticket) => {
    const timeoutId = setTimeout(() => {
      commit('CLEAR_BETSLIP');
      commit('CHANGE_TICKET_TYPE', parseInt(ticket.type, 10));
      commit('TICKET_REBET', ticket);

      // System ticket
      if (parseInt(ticket.type, 10) === 2) {
        ticket.ticketCombinationGroups.forEach((system) => {
          commit('TOGGLE_SYSTEM', system.parlays);
        });
      }
      commit('UPDATE_STAKE', parseFloat(ticket.payin));
      clearTimeout(timeoutId);
    }, 0);
  },
  generateWebTicket: ({ commit, getters }, webCode) => {
    const company = getters.getCompany;
    const cpvUuid = getters.getCpvUuid;
    const productConfig = getters.getProductConfig;

    const route = productConfig ? productConfig.ticket.codeCheckRoute : false;

    // If user never loaded prematch before 'Ticket.CheckWebCode' event
    // Configuration wouldn't be ready in this phase
    // This is well known bug on 7Terminal App
    // 7Terminal should load all iframes on terminal boot
    if (route) {
      axios.get(route, {
        params: {
          cpvUuid,
          webCode,
          locale: company.locale,
        },
      }).then((webTicket) => {
        const ticket = webTicket.data;

        commit('CLEAR_BETSLIP');
        commit('CHANGE_TICKET_TYPE', parseInt(ticket.type, 10));
        commit('TICKET_REBET', ticket);

        const matchIds = getters.getBetslipMatchIds;
        const matchesPromise = axios.all(matchIds.map(matchId => api.loadMatch({
          params: {
            id_match: matchId,
          },
        })));

        matchesPromise.then((matches) => {
          const formattedMatches = matches.map(match => parser.formatMatch(match));
          commit('UPDATE_BETSLIP_ODDS', formattedMatches);
        });

        // System ticket
        if (parseInt(ticket.type, 10) === 2) {
          ticket.ticketCombinationGroups.forEach((system) => {
            commit('TOGGLE_SYSTEM', system.parlays);
          });
        }
        commit('UPDATE_STAKE', parseFloat(ticket.payin));
      });
    }
  },
  handleSocketConnect: ({ commit, getters, dispatch }) => {
    let clientintervalId = getters.getClientIntervalID;
    const clientInterval = getters.getClientServiceTimeout;

    let serverTimeoutId = getters.getServerTimeoutID;
    const serverTimeout = getters.getServerServiceTimeout;

    clearInterval(clientintervalId);
    clientintervalId = setInterval(() => {
      Socket.emit({
        event: 'message',
        options: {
          type: 'serviceAlive',
        },
      });
    }, clientInterval);
    commit('UPDATE_CLIENT_INTERVAL_ID', clientintervalId);

    // Reconnect if backend service has issues
    clearTimeout(serverTimeoutId);
    serverTimeoutId = setTimeout(() => {
      Socket.disconnect();
      const timerId = setTimeout(() => {
        Socket.instance({ dispatch, getters });
        Socket.connect();
        Socket.emit({ event: 'subscribe' });
        clearTimeout(timerId);
      }, 1000);
    }, serverTimeout);

    commit('UPDATE_SERVER_TIMEOUT_ID', serverTimeoutId);
  },
  socketMessage: ({ commit, dispatch, getters }, payload) => {
    let data;

    if (payload.meta.contentEncoding.toUpperCase() === 'DEFLATE') {
      data = decompress(payload.data);
    } else {
      ({ data } = payload);
    }

    const reconnect = ({ timeout, timeoutId }) => {
      clearTimeout(timeoutId);

      const serverTimeoutId = setTimeout(() => {
        Socket.disconnect();
        const timerId = setTimeout(() => {
          Socket.instance({ dispatch, getters });
          Socket.connect();
          Socket.emit({ event: 'subscribe' });
          clearTimeout(timerId);
        }, 1000);
      }, timeout);

      return serverTimeoutId;
    };

    switch (payload.type) {
      case 'serviceReconnect':
        reconnect({ timeout: 0, timeoutId: null });
        break;
      case 'serviceAlive': {
        const timeoutId = getters.getServerTimeoutID;
        const timeout = getters.getServerServiceTimeout;

        const serverTimeoutId = reconnect({ timeout, timeoutId });

        commit('UPDATE_SERVER_TIMEOUT_ID', serverTimeoutId);
        break;
      }
      case 'ticketUpdate': {
        const actionsMapper = {
          cancel: 'Cancel',
          payout: 'Payout',
        };

        const ticketUpdate = {
          action: actionsMapper[data.action] || 'Add',
          printAction: '',
          ticketData: data,
          showMessage: !data.alternativeStake,
        };

        Gateway.sendMessage('Tickets.Update', ticketUpdate);

        if (data.alternativeStake) {
          dispatch('setAlternativeStake', data.alternativeStake);

          Gateway.sendMessage('Dialog.Show', {
            message: data.message,
            type: 'warning',
            showDefaultAction: true,
            actions: [
              {
                id: 'acceptAlternativeStake',
                autoClose: true,
                className: 'cancel_btn',
                text: getters.getTranslation('actions.accept'),
              },
            ],
          }, true);
        }

        if (data.action === 'payout' && getters.getUserType === 'OPERATOR') {
          Object.assign(data, { translation: getters.getTranslation('games.prematchbetting') });

          Gateway.sendMessage('Peripherals.Print', {
            layoutName: 'ticketPreMatchBettingPayout',
            productId: 'PreMatchBetting',
            type: 'ticket',
            action: 'Payout',
            data,
            additionalData: {
              clientPrintJobIdentifier: {
                id: 'TicketPayout',
                uuid: data.requestUuid || '',
              },
            },
          }, true);
        }

        if (data.action === 'add') {
          dispatch('clearBetslip');
        }

        if (getters.getTicketUpdateStatusValues.includes(_.toUpper(data.status.value))) {
          if (_.isEmpty(getters.getCheckedTicket)) return;

          const updatedTicket = {
            ...getters.getCheckedTicket,
            status: data.status,
            payout: data.payout,
            cancellable: data.cancellable,
          };
          dispatch('setTicketCheck', updatedTicket);
        }

        break;
      }
      default:
        commit(`SOCKET_${payload.type.toUpperCase()}`, data);
    }
  },
  closeStatsModal: ({ commit }) => {
    commit('CLOSE_STATS_MODAL');
  },
  openStatsModal: ({ commit }) => {
    commit('OPEN_STATS_MODAL');
  },
  setTicketType: ({ commit }, payload) => {
    commit('SET_TICKET_TYPE', payload);
  },
  setDefaultStake: ({ commit }) => {
    commit('SET_DEFAULT_STAKE');
  },
  removeBet: ({ commit }, payload) => {
    commit('REMOVE_BET', payload);
  },
  addNotification: ({ commit }, payload) => {
    commit('ADD_NOTIFICATION', payload);
  },
  changeTicketType: ({ commit }, payload) => {
    commit('CHANGE_TICKET_TYPE', payload);
  },
  toggleBanker: ({ commit }, payload) => {
    commit('TOGGLE_BANKER', payload);
  },
  toggleBetslip: ({ commit }) => {
    commit('TOGGLE_BETSLIP');
  },
  updateStake: ({ commit, getters }, payload) => {
    const stake = (payload || {}).alternativeStake ? getters.alternativeStake : payload;
    commit('UPDATE_STAKE', stake);
  },
  addBetToBetslip: ({ getters, commit }, payload) => {
    if (getters.workingTimeBlocker) return;

    commit('ADD_BET_TO_BETSLIP', payload);
  },
  toggleSport: ({ commit }, payload) => {
    commit('TOGGLE_SPORT', payload);
  },
  toggleTournament: ({ commit }, payload) => {
    commit('TOGGLE_TOURNAMENT', payload);
  },
  toggleSystem: ({ commit }, payload) => {
    commit('TOGGLE_SYSTEM', payload);
  },
  setActiveSport: ({ commit }, payload) => {
    commit('SET_ACTIVE_SPORT', payload);
  },
  setActiveTournament: ({ commit }, payload) => {
    commit('SET_ACTIVE_TOURNAMENT', payload);
  },
  setActiveCategory: ({ commit }, payload) => {
    commit('SET_ACTIVE_CATEGORY', payload);
  },
  setActiveOutrightsSport: ({ commit }, payload) => {
    commit('SET_ACTIVE_OUTRIGHTS_SPORT', payload);
  },
  toggleOfferLoad: ({ commit }, payload) => {
    commit('TOGGLE_OFFER_LOAD', payload);
  },
  toggleMetaLoad: ({ commit }, payload) => {
    commit('META_LOADED', payload);
  },
  cleanActiveMatch: ({ commit }) => {
    commit('CLEAN_ACTIVE_MATCH');
  },
  updateBalance: ({ commit }, payload) => {
    commit('SET_MONEY_BALANCE', payload);
  },
  setMoneyDenominations: ({ commit }, payload) => {
    commit('SET_MONEY_DENOMINATIONS', payload);
  },
  setOddType: ({ commit }, payload) => {
    commit('SET_ODD_TYPE', payload);
  },
  setDeviceData: ({ commit }, payload) => {
    commit('SET_DEVICE_DATA', payload);
  },
  setCompanyData: ({ commit }, payload) => {
    commit('SET_COMAPANY_DATA', payload);
  },
  setEnv: ({ commit }, payload) => {
    commit('SET_ENV', payload);
  },
  setCompanyConfig: ({ commit }, payload) => {
    commit('SET_COMPANY_CONFIG', payload);
  },
  setBetslipTabs: ({ commit }, payload) => {
    commit('BETSLIP_SET_TABS', payload);
  },
  setWorkingTimeBlocker({ commit, dispatch }, payload) {
    const { message } = _.find(payload, { id: 'WorkingTime' }) || {};
    commit('SET_WORKING_TIME_BLOCKER', message || '');
    dispatch('clearBetslip');
  },
  removeWorkingTimeBlocker({ commit }, payload) {
    const workingTime = _.some(payload, { id: 'WorkingTime' });
    if (workingTime) {
      commit('SET_WORKING_TIME_BLOCKER', '');
    }
  },
  loadCpvUuid: ({ commit }, payload) => {
    commit('LOAD_CPVUUID', payload);
  },
  // Use for closing statistic modal
  // Fired when opening prematch iframe
  iframeShown: ({ commit, getters }) => {
    const statsModalState = getters.getStatsModalState;
    if (statsModalState) {
      commit('CLOSE_STATS_MODAL');
    }
  },
  handleUIShowEvent: (_, data) => {
    const showTicketCheck = ((data || {}).name || []).find(value => value === 'TicketCheck');
    const showBettingArea = ((data || {}).name || []).find(value => value === 'BettingArea');

    if (showBettingArea) {
      router.push('/');
      return;
    }

    if (showTicketCheck) {
      router.push('/ticket-check');
    }
  },
  setTicketCheck: ({ commit }, data) => {
    commit('SET_TICKET_CHECK', data);
  },
  payoutTicket: (_, { ticket }) => {
    Gateway.emitAsync('Tickets.Payout', { ticket });
  },
  cancelTicket: (_, { ticket }) => {
    Gateway.emitAsync('Tickets.Cancel', { ticket });
  },
  rebetTicket: ({ dispatch }, data) => {
    dispatch('ticketRebet', data);

    Gateway.sendMessage('UI.Show', {
      name: ['BettingArea'],
    });
  },
  setUserType: ({ commit }, data) => {
    commit('SET_USER_TYPE', data);
  },
  updateTranslations: ({ commit }, data) => {
    commit('LOAD_TRANSLATIONS', data);
  },
  updateAppSettings: ({ commit }, data) => {
    commit('UPDATE_APP_SETTINGS', data);
  },
  updateCompanyConfig: ({ commit }, data) => {
    commit('SET_COMPANY_CONFIG', data);
  },
  updateTicketType: ({ commit }, data) => {
    commit('SET_TICKET_TYPE', data);
  },
  updateBetslipTabs: ({ commit }, data) => {
    commit('BETSLIP_SET_TABS', data);
  },
  printTicket: ({ getters }, data) => {
    const printPayload = {
      productId: 'PreMatchBetting',
      printAction: 'Add',
      responseData: helpers.prepareForPrint(data, getters),
    };

    return printPayload;
  },
  initApp: ({ getters, dispatch }) => {
    const dateFormat = 'YYYY-MM-DD HH:mm:ss';
    const startDate = new Moment();
    const endDate = new Moment(startDate).add(7, 'days');
    const company = getters.getCompany;

    dispatch('fetchConfiguration', {
      params: {
        locale: company.locale,
        cpvUuid: getters.getCpvUuid,
        deliveryChannel: 'Terminal',
      },
    });

    dispatch('fetchMeta', {
      params: {
        start_date: Moment.utc(startDate).format(dateFormat),
        end_date: Moment.utc(endDate).format(dateFormat),
      },
    });

    Translations.getAll({ storeDispatch: dispatch, company });
    Socket.instance({ dispatch, getters });
    Socket.connect();
    Socket.emit({ event: 'subscribe' });
  },
  setSevenTaxes({ commit }, taxes) {
    commit('SET_SEVEN_TAXES', taxes);
  },
  setAlternativeStake({ commit }, value) {
    commit('SET_ALTERNATIVE_STAKE', value);
  },
};
